// 11 Sep 1906
//
// REFACTORED TO USE CENTRALIZED HOOK
// All complex vitals logic moved to useVitalsForm hook for easier maintenance
//
import { forwardRef, useState, useImperativeHandle } from 'react'
import { Spinner } from 'react-bootstrap'
import {
  Box,
  ButtonBase,
  IconButton,
  Card,
  CardContent,
  Typography,
  Tooltip,
  Button,
} from '@mui/material'
import MonitorHeart from '@mui/icons-material/MonitorHeart'
import ExpandMore from '@mui/icons-material/ExpandMore'
import ExpandLess from '@mui/icons-material/ExpandLess'
import { postCPDataWithParams } from 'src/clinic/services'
import { PMPSectionInputLabel } from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageStyles'
import { useVitalsForm } from 'src/reducers/vitalsFormSettings/hooks/useVitalsForm'
import Print from '@mui/icons-material/Print'
import { Chip } from '@mui/material'

// Legacy constants - will be replaced by dynamic data

// Legacy utility functions - now handled by vitalsUtils

export const VitalsComponentInlineEntryComponent = forwardRef(
  (
    {
      patientId,
      clinicId,
      hidePrintButton = false,
      bookedEventId,
      existingVitalData,
      isPrintable,
      setIsPrintable,
      locallyChangeExistingVitalData,
    },
    ref,
  ) => {
    const [saving, setSaving] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)

    // Use centralized vitals hook with all the complex logic
    const {
      vitalsFields,
      vitalData,
      selectedUnits,
      loading: loadingVitals,
      hasData,
      handleInputChange: hookHandleInputChange,
      handleUnitChange,
      getDisplayValue,
      getDefaultUnit,
      setLoading: setLoadingVitals,
      setVitalData,
    } = useVitalsForm({
      existingVitalData,
      onDataChange: (newVitalData) => {
        // Notify parent component of changes
        locallyChangeExistingVitalData({
          ...newVitalData,
          patientId,
          clinicId,
          bookedEventId,
        })
      },
    })

    // Wrapper to maintain existing API
    const handleInputChange = (fieldId, inputValue, selectedUnit) => {
      hookHandleInputChange(fieldId, inputValue, selectedUnit)
    }

    const saveVital = async () => {
      setSaving(true)
      if (hasData) {
        await postCPDataWithParams(
          '/api/secure/patient/vitals/save',
          {
            ...vitalData,
            patientId,
            clinicId,
            bookedEventId,
          },
          {},
        )
      }
      setSaving(false)
    }

    useImperativeHandle(ref, () => ({ saveVital }))

    const renderVitalInput = (field) => {
      const isDisabled = loadingVitals || saving || field.ui?.readOnly
      const selectedUnit = selectedUnits[field.id] || getDefaultUnit(field)
      const displayValue = getDisplayValue(field)

      // Handle grouped fields (like blood pressure)
      if (field.ui?.groupId === 'bp') {
        const otherBpField = vitalsFields.find((f) => f.ui?.groupId === 'bp' && f.id !== field.id)

        if (field.ui?.groupHeader && otherBpField) {
          const otherDisplayValue = getDisplayValue(otherBpField)

          return (
            <VitalInput
              key={field.id}
              heading={field.ui.groupLabel || field.label}
              inputVal={displayValue || ''}
              inputValTwo={otherDisplayValue || ''}
              onChangeCallback={(d) => handleInputChange(field.id, d, selectedUnit)}
              onChangeCallbackTwo={(d) => handleInputChange(otherBpField.id, d, selectedUnit)}
              unitText={selectedUnit?.label}
              units={field.units}
              selectedUnit={selectedUnit}
              onUnitChange={(unit) => handleUnitChange(field.id, unit)}
              inputDisabled={isDisabled}
              type="dual"
            />
          )
        } else if (!field.ui?.groupHeader) {
          // Skip rendering the second BP field as it's handled above
          return null
        }
      }

      // Handle textarea fields
      const isTextAreaField = field.ui?.input === 'textarea'
      if (isTextAreaField) {
        const inputType = field.type || 'text'
        return (
          <VitalInput
            key={field.id}
            heading={field.label}
            inputVal={displayValue || ''}
            onChangeCallback={(d) => {
              // For textarea fields, bypass numeric validation and set value directly
              setVitalData((prev) => ({
                ...prev,
                [field.id]: d,
              }))
            }}
            inputDisabled={isDisabled}
            type="textarea"
            inputType={inputType}
          />
        )
      }

      // Handle calculated fields
      if (field.type === 'calculated') {
        return (
          <VitalInput
            key={field.id}
            heading={field.label}
            inputVal={displayValue || ''}
            unitText={selectedUnit?.label}
            inputDisabled={true}
            type="single"
            isCalculated={true}
          />
        )
      }

      return (
        <VitalInput
          key={field.id}
          heading={field.label}
          inputVal={displayValue || ''}
          onChangeCallback={(d) => {
            if (field.type === 'text') {
              // For text fields, bypass numeric validation and set value directly
              setVitalData((prev) => ({
                ...prev,
                [field.id]: d,
              }))
            } else {
              // For numeric fields, use normal handling with units
              if (field.ui?.max && d > field.ui.max) return
              handleInputChange(field.id, d || null, selectedUnit)
            }
          }}
          unitText={selectedUnit?.label}
          units={field.units}
          selectedUnit={selectedUnit}
          onUnitChange={(unit) => handleUnitChange(field.id, unit)}
          inputDisabled={isDisabled}
          type="single"
          inputType={field.type || 'number'} // Use field.type for input type
        />
      )
    }

    return (
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, rgba(250, 250, 255, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%)',
          border: '1px solid rgba(67, 56, 202, 0.12)',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(67, 56, 202, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
          overflow: 'visible',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, rgba(67, 56, 202, 0.8) 0%, rgba(99, 102, 241, 0.8) 100%)',
            borderRadius: '16px 16px 0 0',
          },
        }}
        className={isPrintable ? '' : 'hide-on-print'}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Header Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              pb: 2,
              borderBottom: '2px solid rgba(67, 56, 202, 0.1)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
                  color: '#4338ca',
                }}
              >
                <MonitorHeart sx={{ fontSize: 22 }} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: '20px',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    letterSpacing: '-0.01em',
                    mb: 0.5,
                  }}
                >
                  Vitals
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#64748b',
                    fontSize: '12px',
                    fontStyle: 'italic',
                  }}
                >
                  Record patient vital signs and measurements
                </Typography>
              </Box>
              {(loadingVitals || saving) && (
                <Spinner variant="dark" size="sm" style={{ marginLeft: '8px' }} />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {!hidePrintButton && isPrintable && (
                <Tooltip title="Included in printed prescription" arrow placement="top">
                  <Chip
                    icon={<Print sx={{ fontSize: '16px' }} />}
                    label="On Rx"
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      color: '#059669',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      fontWeight: 600,
                      fontSize: '11px',
                      height: '24px',
                      '& .MuiChip-icon': {
                        color: '#059669',
                      },
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Vitals Input Section */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              maxHeight: isExpanded ? '1000px' : '100px',
              overflow: 'hidden',
              transition: (theme) =>
                theme.transitions.create('max-height', {
                  easing: theme.transitions.easing.quickInOut,
                  duration: '0.3s',
                }),
              pt: 1,
            }}
          >
            {vitalsFields.map(renderVitalInput).filter(Boolean)}
          </Box>

          {/* Bottom expand/collapse control — uses the same centered bottom action-bar
              construct as the other sections (e.g. Diagnosis' add bar) instead of a
              separate top-right Expand button, so every section reads the same way. */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 1.5,
              pt: 1,
              borderTop: '1px solid rgba(67, 56, 202, 0.1)',
            }}
          >
            <Button
              onClick={() => setIsExpanded(!isExpanded)}
              variant="text"
              size="small"
              endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
              sx={{
                textTransform: 'none',
                color: '#4338ca',
                fontWeight: 600,
                '&:hover': { backgroundColor: 'rgba(67, 56, 202, 0.06)' },
              }}
            >
              {isExpanded ? 'Show less' : 'Show all vitals'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    )
  },
)

// Enhanced input component with unit selection
const VitalInput = ({
  heading,
  inputVal,
  inputValTwo,
  onChangeCallback,
  onChangeCallbackTwo,
  unitText,
  units = [],
  selectedUnit,
  onUnitChange,
  helperText,
  inputDisabled = false,
  type = 'single',
  isCalculated = false,
  inputType = 'number',
}) => {
  const commonInputStyle = {
    background: inputDisabled ? '#f3f4f6' : '#ffffff',
    width: '55px',
    minHeight: '36px',
    borderRadius: '8px',
    padding: '8px 10px',
    borderTop: inputDisabled
      ? '1px solid #e5e7eb'
      : '1px solid rgba(67, 56, 202, 0.2)',
    borderBottom: inputDisabled
      ? '1px solid #e5e7eb'
      : '1px solid rgba(67, 56, 202, 0.2)',
    borderLeft: inputDisabled
      ? '1px solid #e5e7eb'
      : '1px solid rgba(67, 56, 202, 0.2)',
    borderRight: inputDisabled
      ? '1px solid #e5e7eb'
      : '1px solid rgba(67, 56, 202, 0.2)',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: '500',
    color: inputDisabled ? '#9ca3af' : '#1e293b',
    transition: 'all 0.2s ease',
    outline: 'none',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  }

  const commonInputFocusStyle = {
    borderTopColor: '#4338ca',
    borderBottomColor: '#4338ca',
    borderLeftColor: '#4338ca',
    borderRightColor: '#4338ca',
    boxShadow: '0 0 0 3px rgba(67, 56, 202, 0.1)',
  }

  const showUnitDropdown = units && units.length > 1 && !isCalculated

  const textareaStyle = {
    background: inputDisabled ? '#f3f4f6' : '#ffffff',
    width: '100%',
    minWidth: '300px',
    minHeight: '80px',
    borderRadius: '8px',
    padding: '12px',
    resize: 'vertical',
    fontFamily: 'inherit',
    fontSize: '14px',
    borderTop: inputDisabled
      ? '1px solid #e5e7eb'
      : '1px solid rgba(67, 56, 202, 0.2)',
    borderBottom: inputDisabled
      ? '1px solid #e5e7eb'
      : '1px solid rgba(67, 56, 202, 0.2)',
    borderLeft: inputDisabled
      ? '1px solid #e5e7eb'
      : '1px solid rgba(67, 56, 202, 0.2)',
    borderRight: inputDisabled
      ? '1px solid #e5e7eb'
      : '1px solid rgba(67, 56, 202, 0.2)',
    color: inputDisabled ? '#9ca3af' : '#1e293b',
    transition: 'all 0.2s ease',
    outline: 'none',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  }

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    background: 'rgba(255, 255, 255, 0.5)',
    borderRadius: '12px',
    border: '1px solid rgba(67, 56, 202, 0.08)',
    transition: 'all 0.2s ease',
  }

  const labelStyle = {
    fontSize: '13px',
    textAlign: 'center',
    fontWeight: '600',
    color: '#4338ca',
    letterSpacing: '0.01em',
  }

  const unitDisplayStyle = {
    padding: '8px 10px',
    fontSize: '12px',
    fontWeight: '500',
    background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.08) 0%, rgba(99, 102, 241, 0.08) 100%)',
    color: '#4338ca',
    borderTop: '1px solid rgba(67, 56, 202, 0.15)',
    borderBottom: '1px solid rgba(67, 56, 202, 0.15)',
    borderLeft: '1px solid rgba(67, 56, 202, 0.15)',
    borderRight: '1px solid rgba(67, 56, 202, 0.15)',
    borderRadius: '0 8px 8px 0',
    minWidth: '50px',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const unitSelectStyle = {
    padding: '8px 10px',
    fontSize: '12px',
    fontWeight: '500',
    borderTop: '1px solid rgba(67, 56, 202, 0.2)',
    borderBottom: '1px solid rgba(67, 56, 202, 0.2)',
    borderLeft: 'none',
    borderRight: '1px solid rgba(67, 56, 202, 0.2)',
    background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.08) 0%, rgba(99, 102, 241, 0.08) 100%)',
    color: '#4338ca',
    borderRadius: '0 8px 8px 0',
    minWidth: '60px',
    cursor: 'pointer',
    outline: 'none',
    transition: 'all 0.2s ease',
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        p: 1.5,
        background: 'rgba(255, 255, 255, 0.6)',
        borderRadius: '12px',
        borderTop: '1px solid rgba(67, 56, 202, 0.1)',
        borderBottom: '1px solid rgba(67, 56, 202, 0.1)',
        borderLeft: '1px solid rgba(67, 56, 202, 0.1)',
        borderRight: '1px solid rgba(67, 56, 202, 0.1)',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderTopColor: 'rgba(67, 56, 202, 0.2)',
          borderBottomColor: 'rgba(67, 56, 202, 0.2)',
          borderLeftColor: 'rgba(67, 56, 202, 0.2)',
          borderRightColor: 'rgba(67, 56, 202, 0.2)',
          boxShadow: '0 2px 8px rgba(67, 56, 202, 0.1)',
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: '13px',
          fontWeight: 600,
          color: '#4338ca',
          textAlign: 'center',
          letterSpacing: '0.01em',
        }}
      >
        {heading}
      </Typography>

      {type === 'textarea' ? (
        <textarea
          disabled={inputDisabled}
          value={inputVal || ''}
          style={textareaStyle}
          placeholder={`Enter ${heading.toLowerCase()}...`}
          onChange={(e) => onChangeCallback && onChangeCallback(e.target.value)}
          onFocus={(e) => {
            if (!inputDisabled) {
              e.target.style.borderTopColor = '#4338ca'
              e.target.style.borderBottomColor = '#4338ca'
              e.target.style.borderLeftColor = '#4338ca'
              e.target.style.borderRightColor = '#4338ca'
              e.target.style.boxShadow = '0 0 0 3px rgba(67, 56, 202, 0.1)'
            }
          }}
          onBlur={(e) => {
            e.target.style.borderTopColor = 'rgba(67, 56, 202, 0.2)'
            e.target.style.borderBottomColor = 'rgba(67, 56, 202, 0.2)'
            e.target.style.borderLeftColor = 'rgba(67, 56, 202, 0.2)'
            e.target.style.borderRightColor = 'rgba(67, 56, 202, 0.2)'
            e.target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'
          }}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            borderRadius: '8px',
            overflow: 'hidden',
            borderTop: inputDisabled
              ? '1px solid #e5e7eb'
              : '1px solid rgba(67, 56, 202, 0.2)',
            borderBottom: inputDisabled
              ? '1px solid #e5e7eb'
              : '1px solid rgba(67, 56, 202, 0.2)',
            borderLeft: inputDisabled
              ? '1px solid #e5e7eb'
              : '1px solid rgba(67, 56, 202, 0.2)',
            borderRight: inputDisabled
              ? '1px solid #e5e7eb'
              : '1px solid rgba(67, 56, 202, 0.2)',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderTopColor: inputDisabled ? '#e5e7eb' : 'rgba(67, 56, 202, 0.3)',
              borderBottomColor: inputDisabled ? '#e5e7eb' : 'rgba(67, 56, 202, 0.3)',
              borderLeftColor: inputDisabled ? '#e5e7eb' : 'rgba(67, 56, 202, 0.3)',
              borderRightColor: inputDisabled ? '#e5e7eb' : 'rgba(67, 56, 202, 0.3)',
            },
            '&:focus-within': {
              borderTopColor: '#4338ca',
              borderBottomColor: '#4338ca',
              borderLeftColor: '#4338ca',
              borderRightColor: '#4338ca',
              boxShadow: '0 0 0 3px rgba(67, 56, 202, 0.1)',
            },
          }}
        >
          {type === 'dual' ? (
            <>
              <input
                disabled={inputDisabled}
                type="number"
                value={inputVal || ''}
                style={{
                  ...commonInputStyle,
                  width: '55px',
                  borderRadius: '8px 0 0 8px',
                  borderRight: 'none',
                }}
                onChange={(e) => onChangeCallback && onChangeCallback(e.target.value)}
                onFocus={(e) => {
                  if (!inputDisabled) {
                    Object.assign(e.target.style, commonInputFocusStyle)
                  }
                }}
                onBlur={(e) => {
                  const borderColor = inputDisabled
                    ? '#e5e7eb'
                    : 'rgba(67, 56, 202, 0.2)'
                  e.target.style.borderTopColor = borderColor
                  e.target.style.borderBottomColor = borderColor
                  e.target.style.borderLeftColor = borderColor
                  e.target.style.borderRightColor = borderColor
                  e.target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
              />
              <Box
                sx={{
                  width: '20px',
                  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.08) 0%, rgba(99, 102, 241, 0.08) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#4338ca',
                  borderLeft: '1px solid rgba(67, 56, 202, 0.1)',
                  borderRight: '1px solid rgba(67, 56, 202, 0.1)',
                }}
              >
                /
              </Box>
              <input
                disabled={inputDisabled}
                type="number"
                value={inputValTwo || ''}
                style={{
                  ...commonInputStyle,
                  width: '55px',
                  borderRadius: '0',
                  borderRight: unitText || showUnitDropdown ? 'none' : '1px solid rgba(67, 56, 202, 0.2)',
                }}
                onChange={(e) => onChangeCallbackTwo && onChangeCallbackTwo(e.target.value)}
                onFocus={(e) => {
                  if (!inputDisabled) {
                    Object.assign(e.target.style, commonInputFocusStyle)
                  }
                }}
                onBlur={(e) => {
                  const borderColor = inputDisabled
                    ? '#e5e7eb'
                    : 'rgba(67, 56, 202, 0.2)'
                  e.target.style.borderTopColor = borderColor
                  e.target.style.borderBottomColor = borderColor
                  e.target.style.borderLeftColor = borderColor
                  e.target.style.borderRightColor = borderColor
                  e.target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
              />
            </>
          ) : (
            <input
              disabled={inputDisabled}
              type={inputType}
              value={inputVal || ''}
              style={{
                ...commonInputStyle,
                borderRadius: unitText || showUnitDropdown ? '8px 0 0 8px' : '8px',
                borderRight: unitText || showUnitDropdown ? 'none' : '1px solid rgba(67, 56, 202, 0.2)',
              }}
              onChange={(e) => onChangeCallback && onChangeCallback(e.target.value)}
              onFocus={(e) => {
                if (!inputDisabled) {
                  Object.assign(e.target.style, commonInputFocusStyle)
                }
              }}
              onBlur={(e) => {
                const borderColor = inputDisabled
                  ? '#e5e7eb'
                  : 'rgba(67, 56, 202, 0.2)'
                e.target.style.borderTopColor = borderColor
                e.target.style.borderBottomColor = borderColor
                e.target.style.borderLeftColor = borderColor
                e.target.style.borderRightColor = borderColor
                e.target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'
              }}
            />
          )}

          {showUnitDropdown ? (
            <select
              value={selectedUnit?.code || ''}
              onChange={(e) => {
                const newUnit = units.find((u) => u.code === e.target.value)
                onUnitChange && onUnitChange(newUnit)
              }}
              style={unitSelectStyle}
              onFocus={(e) => {
                e.target.style.borderTopColor = '#4338ca'
                e.target.style.borderBottomColor = '#4338ca'
                e.target.style.borderLeftColor = '#4338ca'
                e.target.style.borderRightColor = '#4338ca'
                e.target.style.boxShadow = '0 0 0 3px rgba(67, 56, 202, 0.1)'
              }}
              onBlur={(e) => {
                e.target.style.borderTopColor = 'rgba(67, 56, 202, 0.2)'
                e.target.style.borderBottomColor = 'rgba(67, 56, 202, 0.2)'
                e.target.style.borderLeftColor = 'rgba(67, 56, 202, 0.2)'
                e.target.style.borderRightColor = 'rgba(67, 56, 202, 0.2)'
                e.target.style.boxShadow = 'none'
              }}
            >
              {units.map((unit) => (
                <option key={unit.code} value={unit.code}>
                  {unit.label}
                </option>
              ))}
            </select>
          ) : unitText ? (
            <Box sx={unitDisplayStyle}>{unitText}</Box>
          ) : null}
        </Box>
      )}

      {helperText && (
        <Typography
          variant="caption"
          sx={{
            color: '#64748b',
            fontSize: '10px',
            textAlign: 'center',
            fontStyle: 'italic',
          }}
        >
          {helperText}
        </Typography>
      )}

      {isCalculated && (
        <Typography
          variant="caption"
          sx={{
            color: '#10b981',
            fontSize: '10px',
            textAlign: 'center',
            fontWeight: 500,
            fontStyle: 'italic',
          }}
        >
          (calculated)
        </Typography>
      )}
    </Box>
  )
}
